                                                                                                                                                                                import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { networkDevicesAPI } from '../services/api';
import {
  NetworkDevice,
  NetworkPort,
  NetworkPortStatus,
  NetworkPortType,
  NetworkDeviceStatus
} from '../types';

const portTypeOptions: { label: string; value: NetworkPortType }[] = [
  { label: 'Ethernet', value: 'ethernet' },
  { label: 'Fiber', value: 'fiber' },
  { label: 'Console', value: 'console' },
  { label: 'Management', value: 'management' }
];

const portStatusOptions: { label: string; value: NetworkPortStatus }[] = [
  { label: 'Up', value: 'up' },
  { label: 'Down', value: 'down' },
  { label: 'Disabled', value: 'disabled' }
];

const deviceStatusSeverity: Record<NetworkDeviceStatus, 'success' | 'secondary' | 'warning' | 'danger'> = {
  active: 'success',
  inactive: 'secondary',
  maintenance: 'warning',
  retired: 'danger'
};

const ITPortConfig: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [devices, setDevices] = useState<NetworkDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const [portsVisible, setPortsVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<NetworkDevice | null>(null);
  const [ports, setPorts] = useState<NetworkPort[]>([]);
  const [savingPorts, setSavingPorts] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await networkDevicesAPI.getAll();
      setDevices(data);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju mrežnih uređaja');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openPorts = (device: NetworkDevice) => {
    setSelectedDevice(device);
    const sortedPorts = [...(device.ports || [])].sort((a, b) => a.portNumber - b.portNumber);
    setPorts(sortedPorts);
    setPortsVisible(true);
  };

  const updatePortField = (index: number, field: keyof NetworkPort, value: any) => {
    setPorts((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const addPort = () => {
    setPorts((prev) => [
      ...prev,
      {
        _id: Math.random().toString(36).slice(2),
        portNumber: prev.length ? Math.max(...prev.map((p) => p.portNumber)) + 1 : 1,
        portType: 'ethernet',
        status: 'up'
      }
    ]);
  };

  const removePort = (index: number) => {
    setPorts((prev) => prev.filter((_, i) => i !== index));
  };

  const canSavePorts = useMemo(
    () => !!selectedDevice && ports.every((p) => p.portNumber > 0),
    [ports, selectedDevice]
  );

  const handleSavePorts = async () => {
    if (!selectedDevice || !canSavePorts) return;
    setSavingPorts(true);
    try {
      await networkDevicesAPI.update(selectedDevice._id, { ports });
      toast.current?.show({
        severity: 'success',
        summary: 'Uspešno',
        detail: 'Port konfiguracija sačuvana',
        life: 2500
      });
      setPortsVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju port konfiguracije',
        life: 5000
      });
    } finally {
      setSavingPorts(false);
    }
  };

  const filteredDevices = devices.filter((d) => {
    const nameMatch = !nameFilter || d.name.toLowerCase().includes(nameFilter.toLowerCase());
    const typeMatch = !typeFilter || d.type.toLowerCase().includes(typeFilter.toLowerCase());
    const ipMatch = !ipFilter || (d.ipAddress || '').toLowerCase().includes(ipFilter.toLowerCase());
    const locationMatch =
      !locationFilter || d.location.toLowerCase().includes(locationFilter.toLowerCase());
    return nameMatch && typeMatch && ipMatch && locationMatch;
  });

  const deviceStatusBody = (row: NetworkDevice) => (
    <Tag
      value={row.status === 'active' ? 'Aktivan' : row.status === 'inactive' ? 'Neaktivan' : row.status}
      severity={deviceStatusSeverity[row.status]}
    />
  );

  const portsCountBody = (row: NetworkDevice) => {
    const count = row.ports?.length ?? 0;
    return <span>{count}</span>;
  };

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <Card>
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h2 className="m-0">Port Konfiguracija - Mrežni Uređaji</h2>
        </div>

        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="grid mb-3">
          <div className="col-12 md:col-3">
            <InputText
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Naziv"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-3">
            <InputText
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              placeholder="Tip"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-3">
            <InputText
              value={ipFilter}
              onChange={(e) => setIpFilter(e.target.value)}
              placeholder="IP Adresa"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-3">
            <InputText
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              placeholder="Lokacija"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <DataTable
          value={filteredDevices}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema mrežnih uređaja"
          className="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="type" header="Tip" sortable />
          <Column field="ipAddress" header="IP Adresa" sortable />
          <Column field="location" header="Lokacija" sortable />
          <Column header="Broj portova" body={portsCountBody} sortable />
          <Column field="status" header="Status" body={deviceStatusBody} sortable />
          <Column
            header="Akcije"
            body={(row: NetworkDevice) => (
              <Button
                icon="pi pi-sliders-h"
                label="Konfiguriši"
                className="p-button-sm"
                onClick={() => openPorts(row)}
              />
            )}
            style={{ width: '180px' }}
          />
        </DataTable>
      </Card>

      <Dialog
        header={
          selectedDevice
            ? `Port konfiguracija - ${selectedDevice.name}`
            : 'Port konfiguracija'
        }
        visible={portsVisible}
        onHide={() => setPortsVisible(false)}
        style={{ width: '900px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => setPortsVisible(false)}
            />
            <Button
              label="Sačuvaj"
              icon="pi pi-check"
              onClick={handleSavePorts}
              disabled={!canSavePorts}
              loading={savingPorts}
            />
          </div>
        }
      >
        {!selectedDevice ? (
          <p>Izaberite uređaj za konfiguraciju portova.</p>
        ) : (
          <>
            {ports.length === 0 && (
              <p className="mb-3">Ovaj uređaj nema definisane portove. Dodajte nove portove.</p>
            )}
            <div className="flex justify-content-end mb-2">
              <Button
                label="Dodaj port"
                icon="pi pi-plus"
                size="small"
                onClick={addPort}
              />
            </div>
            <div className="table-responsive">
              <table className="port-config-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '0.5rem' }}>Port</th>
                    <th style={{ padding: '0.5rem' }}>Tip</th>
                    <th style={{ padding: '0.5rem' }}>VLAN</th>
                    <th style={{ padding: '0.5rem' }}>Status</th>
                    <th style={{ padding: '0.5rem' }}>Opis</th>
                    <th style={{ padding: '0.5rem' }}>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {ports.map((port, index) => (
                    <tr key={port._id || index}>
                      <td style={{ padding: '0.25rem' }}>
                        <InputText
                          value={String(port.portNumber)}
                          onChange={(e) =>
                            updatePortField(index, 'portNumber', Number(e.target.value) || 0)
                          }
                          style={{ width: '5rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.25rem' }}>
                        <Dropdown
                          value={port.portType}
                          options={portTypeOptions}
                          optionLabel="label"
                          optionValue="value"
                          onChange={(e) => updatePortField(index, 'portType', e.value)}
                          appendTo="self"
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ padding: '0.25rem' }}>
                        <InputText
                          value={port.vlan !== undefined ? String(port.vlan) : ''}
                          onChange={(e) =>
                            updatePortField(
                              index,
                              'vlan',
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          style={{ width: '5rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.25rem' }}>
                        <Dropdown
                          value={port.status}
                          options={portStatusOptions}
                          optionLabel="label"
                          optionValue="value"
                          onChange={(e) => updatePortField(index, 'status', e.value)}
                          appendTo="self"
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ padding: '0.25rem' }}>
                        <InputText
                          value={port.description || ''}
                          onChange={(e) => updatePortField(index, 'description', e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </td>
                      <td style={{ padding: '0.25rem' }}>
                        <Button
                          icon="pi pi-trash"
                          className="p-button-text p-button-danger p-button-sm"
                          onClick={() => removePort(index)}
                          tooltip="Ukloni port"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default ITPortConfig;






